import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFilePdf,
  faFileImage,
  faFileWord,
  faFileExcel,
  faFileAlt,
  faEye,
  faDownload,
  faBan,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  getCabinetMinistre,
  deleteCabinetMinistre,
} from "../../../services/api";
import { CabinetMinistre } from "../../../types/types";
import { toast } from "react-toastify";
import { useLanguage } from "../../../context/LanguageContext";
import { getTranslation } from "../../../utils/translations";
import AdminLayout from "../AdminLayout";
import {
  getFileExtension,
  getFileName,
  isImageFile,
  resolveMediaUrl,
} from "../../../utils/media";

// Function to extract plain text from HTML
const extractPlainText = (html: string): string => {
  if (!html) return "";

  let plainText = html.replace(/<[^>]*>/g, " ");
  plainText = plainText
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'");

  return plainText.replace(/\s+/g, " ").trim();
};

// Helper function to detect file type
const detectFileType = (fileData: string): string => {
  if (!fileData) return "unknown";
  const extension = getFileExtension(fileData);
  if (extension === "pdf") return "pdf";
  if (["jpg", "jpeg"].includes(extension)) return "image/jpeg";
  if (extension === "png") return "image/png";
  if (extension === "gif") return "image/gif";
  if (["doc", "docx"].includes(extension)) return "word";
  if (["xls", "xlsx"].includes(extension)) return "excel";
  return "unknown";
};

// Helper function to get appropriate file icon
const getFileIcon = (fileType: string) => {
  if (fileType === "pdf") return faFilePdf;
  if (fileType.includes("image/")) return faFileImage;
  if (fileType === "word") return faFileWord;
  if (fileType === "excel") return faFileExcel;
  return faFileAlt;
};

// Helper function to get file type display name
const getFileTypeName = (fileType: string): string => {
  if (fileType === "pdf") return "ملف PDF";
  if (fileType === "image/jpeg") return "صورة JPEG";
  if (fileType === "image/png") return "صورة PNG";
  if (fileType === "image/gif") return "صورة GIF";
  if (fileType === "word") return "وثيقة Word";
  if (fileType === "excel") return "ملف Excel";
  return "ملف";
};

const CabinetMinistreList: React.FC = () => {
  const [cabinetItem, setCabinetItem] = useState<CabinetMinistre | null>(null);
  const [loading, setLoading] = useState(true);
  const [fileType, setFileType] = useState<string>("unknown");
  const { language } = useLanguage();

  useEffect(() => {
    fetchCabinetItem();
  }, []);

  const fetchCabinetItem = async () => {
    try {
      setLoading(true);
      const response = await getCabinetMinistre();
      // Take only the first item if it exists
      if (response.data && response.data.length > 0) {
        const item = response.data[0];
        setCabinetItem(item);

        // Detect file type
        if (item.fichier) {
          const detectedType = detectFileType(item.fichier);
          setFileType(detectedType);
        }
      } else {
        setCabinetItem(null);
        setFileType("unknown");
      }
    } catch (error) {
      console.error("Error fetching cabinet item:", error);
      toast.error("حدث خطأ أثناء جلب بيانات مكتب الوزير");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("هل أنت متأكد من حذف سجل مكتب الوزير؟")) {
      try {
        await deleteCabinetMinistre(id);
        toast.success("تم حذف سجل مكتب الوزير بنجاح");
        setCabinetItem(null);
        setFileType("unknown");
      } catch (error) {
        console.error("Error deleting cabinet item:", error);
        toast.error("حدث خطأ أثناء حذف السجل");
      }
    }
  };

  const handleViewFile = () => {
    if (cabinetItem?.fichier) {
      window.open(resolveMediaUrl(cabinetItem.fichier), "_blank");
    }
  };

  const handleDownloadFile = () => {
    if (cabinetItem?.fichier) {
      const link = document.createElement("a");
      link.href = resolveMediaUrl(cabinetItem.fichier);
      link.download = getFileName(cabinetItem.fichier) || "cabinet-file";
      link.click();
    }
  };

  const getPlainDescription = (
    html: string,
    maxLength: number = 100,
  ): string => {
    const plainText = extractPlainText(html);
    if (plainText.length > maxLength) {
      return plainText.substring(0, maxLength) + "...";
    }
    return plainText;
  };

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark mb-4 md:mb-0">
          {getTranslation("adminCabinetTitle", language)}
        </h1>

        {/* Show Add button only when no record exists */}
        {!cabinetItem ? (
          <Link
            to="/admin/cabinetministre/new"
            className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300"
          >
            <FontAwesomeIcon icon={faPlus} className="ml-2" />
            {getTranslation("addCabinetEntry", language)}
          </Link>
        ) : (
          <div className="flex items-center space-x-4 space-x-reverse">
            {/* <Link
              to={`/admin/cabinetministre/edit/${cabinetItem.id}`} // Fixed: Added item ID
              className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300"
            >
              <FontAwesomeIcon icon={faEdit} className="ml-2" />
              تعديل السجل
            </Link>
            <button
              onClick={() => handleDelete(cabinetItem.id!)}
              className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition duration-300"
            >
              <FontAwesomeIcon icon={faTrash} className="ml-2" />
              حذف السجل
            </button> */}
          </div>
        )}
      </div>

      {/* Info box explaining single record limitation */}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-mauritania-green"></div>
          <p className="mt-2 text-gray-600">
            {getTranslation("loadingData", language)}
          </p>
        </div>
      ) : !cabinetItem ? (
        // Empty state - No record exists
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <div className="inline-block bg-gray-100 p-6 rounded-full mb-4">
            <FontAwesomeIcon
              icon={faFileAlt}
              className="text-gray-400 text-3xl"
            />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            لا يوجد سجل لمكتب الوزير
          </h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            لم يتم إضافة أي سجل لمكتب الوزير بعد. يمكنك إضافة سجل واحد فقط.
          </p>
          <Link
            to="/admin/cabinetministre/new"
            className="inline-flex items-center bg-mauritania-green text-white py-2 px-6 rounded hover:bg-mauritania-green-dark transition duration-300"
          >
            <FontAwesomeIcon icon={faPlus} className="ml-2" />
            إضافة سجل جديد
          </Link>
        </div>
      ) : (
        // Show the single record
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
              <h2 className="text-2xl font-bold text-mauritania-green-dark mb-4 md:mb-0">
                تعديل مكتب الوزير
              </h2>
              <div className="flex space-x-3 space-x-reverse">
                <Link
                  to={`/admin/cabinetministre/edit/${cabinetItem.id}`}
                  className="inline-flex items-center text-mauritania-green hover:text-mauritania-green-dark font-medium"
                >
                  <FontAwesomeIcon icon={faEdit} className="ml-1" />
                  تعديل السجل
                </Link>
                <button
                  onClick={() => handleDelete(cabinetItem.id!)}
                  className="inline-flex items-center text-red-600 hover:text-red-900 font-medium"
                >
                  <FontAwesomeIcon icon={faTrash} className="ml-1" />
                  حذف السجل
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Titles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    العنوان (عربي)
                  </h3>
                  <p className="text-lg font-semibold text-gray-900">
                    {cabinetItem.titrearabe}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    العنوان (فرنسي)
                  </h3>
                  <p className="text-lg font-semibold text-gray-900">
                    {cabinetItem.titrefrancais}
                  </p>
                </div>
              </div>

              {/* Descriptions */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  الوصف (عربي)
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg prose prose-sm max-w-none">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: cabinetItem.descarabe || "",
                    }}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">
                  الوصف (فرنسي)
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg prose prose-sm max-w-none">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: cabinetItem.descfrancais || "",
                    }}
                  />
                </div>
              </div>

              {/* File Attachment */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">
                  الملف المرفق
                </h3>
                {cabinetItem.fichier ? (
                  <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-4 space-x-reverse p-4 bg-gray-50 rounded-lg">
                    {/* File Preview/Thumbnail */}
                    <div className="flex-shrink-0">
                      {isImageFile(cabinetItem.fichier) || fileType.includes("image/") ? (
                        <div className="w-24 h-24 rounded-lg overflow-hidden border border-gray-200">
                          <img
                            src={
                              resolveMediaUrl(cabinetItem.fichier)
                            }
                            alt="ملف مرفق"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-24 h-24 rounded-lg bg-white border border-gray-200 flex flex-col items-center justify-center p-3">
                          <FontAwesomeIcon
                            icon={getFileIcon(fileType)}
                            className="text-mauritania-green text-3xl mb-2"
                          />
                          <span className="text-xs text-gray-600 text-center">
                            {getFileTypeName(fileType)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* File Info */}
                    <div className="flex-grow">
                      <div className="mb-2">
                        <p className="text-sm font-medium text-gray-900">
                          {getFileTypeName(fileType)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {cabinetItem.fichier.length > 1000
                            ? `حجم الملف: ${Math.round(
                                cabinetItem.fichier.length / 1024,
                              )} كيلوبايت`
                            : "ملف مرفق"}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-3 space-x-reverse">
                        <button
                          onClick={handleViewFile}
                          className="inline-flex items-center text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded text-sm"
                          title={`${getTranslation("preview", language)} الملف`}
                        >
                          <FontAwesomeIcon icon={faEye} className="ml-1" />
                          {getTranslation("preview", language)}
                        </button>
                        <button
                          onClick={handleDownloadFile}
                          className="inline-flex items-center text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-3 py-1 rounded text-sm"
                          title={`${getTranslation("download", language)} الملف`}
                        >
                          <FontAwesomeIcon icon={faDownload} className="ml-1" />
                          {getTranslation("download", language)}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-50 rounded-lg flex items-center">
                    <FontAwesomeIcon
                      icon={faFileAlt}
                      className="text-gray-400 ml-3"
                    />
                    <p className="text-gray-500">لا يوجد ملف مرفق</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CabinetMinistreList;
